import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Wallet, Users, Calculator, FileText, Landmark, ShieldCheck, LayoutDashboard } from "lucide-react";
import { HorizontalBarCard, LineCard, COLORS as CHART_COLORS } from "@/components/charts/DashboardCharts";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
API.interceptors.request.use((c) => { const t = localStorage.getItem("abs_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const rp = (n) => (n == null ? "-" : `Rp ${Number(n).toLocaleString("id-ID", { minimumFractionDigits: 0 })}`);
const statusColor = (s) => ({
  DRAFT: "bg-slate-500", CALCULATED: "bg-blue-500", REVIEW: "bg-amber-500",
  APPROVED: "bg-emerald-500", POSTED: "bg-teal-600", PAID: "bg-green-700", LOCKED: "bg-gray-700",
  READY_TO_PAY: "bg-indigo-500", SESUAI: "bg-emerald-500", PERLU_DITINJAU: "bg-amber-500", ERROR: "bg-red-600",
  ACTIVE: "bg-emerald-500", LUNAS: "bg-slate-500",
}[s] || "bg-slate-500");

// ============ HUB ============
export function PayrollHub() {
  const cards = [
    { to: "/payroll/dashboard", icon: LayoutDashboard, title: "Dasbor Payroll", desc: "Ringkasan periode, PPh21, BPJS, take-home." },
    { to: "/payroll/komponen", icon: FileText, title: "Komponen Payroll", desc: "Master EARNING & DEDUCTION (versioned)." },
    { to: "/payroll/struktur-gaji", icon: Users, title: "Struktur Gaji", desc: "Set salary per karyawan (versioned)." },
    { to: "/payroll/periode", icon: Calculator, title: "Periode Payroll", desc: "Kalkulasi, review, approval, posting." },
    { to: "/payroll/pinjaman", icon: Wallet, title: "Pinjaman / Kasbon", desc: "Loan management + installment tracking." },
    { to: "/payroll/payment-batch", icon: Landmark, title: "Payment Batch", desc: "Batch pembayaran + bank match." },
    { to: "/payroll/slip-gaji", icon: FileText, title: "Slip Gaji Saya (ESS)", desc: "Lihat slip gaji Anda sendiri." },
    { to: "/payroll/integritas", icon: ShieldCheck, title: "Integritas Payroll", desc: "20-way integrity center." },
  ];
  return (
    <div className="p-6 space-y-6" data-testid="payroll-hub">
      <h1 className="text-3xl font-bold">Payroll — Tahap 8C & 8D</h1>
      <p className="text-slate-500">Payroll Engine + Payroll Integration (Accounting, Bank, Tax).</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.to} to={c.to} data-testid={`nav-${c.to.replace(/\//g, "-")}`}>
              <Card className="hover:shadow-md transition">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                  <Icon className="h-6 w-6 text-slate-700" />
                  <CardTitle className="text-base">{c.title}</CardTitle>
                </CardHeader>
                <CardContent><p className="text-sm text-slate-600">{c.desc}</p></CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ============ Dashboard ============
export function PayrollDashboard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setErr(null);
    API.get(`/payroll/dashboard?year=${year}`)
      .then((r) => setD(r.data))
      .catch((e) => {
        if (e?.response?.status === 403) {
          setErr("Tidak berwenang mengakses Dasbor Payroll — perlu izin HR_PAYROLL_LIHAT. Hubungi administrator jika Anda membutuhkan akses.");
        } else {
          setErr("Gagal memuat data payroll.");
        }
      });
  }, [year]);
  const monthly = (d?.periods || []).map((p) => ({
    name: p.period_key, gross: Number(p.gross || 0), net: Number(p.net || 0),
  }));
  const byDept = (d?.by_department || []).map((x) => ({
    name: x.department || "-", gross: Number(x.gross || 0),
  }));
  return (
    <div className="p-6 space-y-4" data-testid="payroll-dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dasbor Payroll {year}</h1>
        <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)} className="w-32" data-testid="year-input" />
      </div>
      {err && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="payroll-no-access">
          {err}
        </div>
      )}
      {d && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4"><div className="text-sm text-slate-500">Jumlah Karyawan Payroll</div><div className="text-2xl font-bold" data-testid="total-headcount">{d.totals.employee_count || 0}</div></CardContent></Card>
            <Card><CardContent className="pt-4"><div className="text-sm text-slate-500">Total Gross Setahun</div><div className="text-2xl font-bold" data-testid="total-gross">{rp(d.totals.gross)}</div></CardContent></Card>
            <Card><CardContent className="pt-4"><div className="text-sm text-slate-500">Total Take-Home</div><div className="text-2xl font-bold text-emerald-700" data-testid="total-net">{rp(d.totals.net)}</div></CardContent></Card>
            <Card><CardContent className="pt-4"><div className="text-sm text-slate-500">Total PPh 21</div><div className="text-2xl font-bold text-amber-700" data-testid="total-pph21">{rp(d.totals.pph21)}</div></CardContent></Card>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LineCard
              title="Payroll per Bulan"
              testId="chart-payroll-monthly"
              hint="Belum ada periode payroll untuk tahun ini."
              currency
              data={monthly}
              lines={[
                { key: "gross", label: "Gross", color: CHART_COLORS.baseline },
                { key: "net", label: "Net (Take-Home)", color: CHART_COLORS.positive },
              ]}
            />
            <HorizontalBarCard
              title="Payroll per Departemen"
              testId="chart-payroll-department"
              hint="Belum ada data payroll per departemen."
              currency
              data={byDept}
              bars={[{ key: "gross", label: "Gross", color: CHART_COLORS.baseline }]}
              height={Math.max(220, byDept.length * 42 + 60)}
            />
          </div>
          <Card><CardHeader><CardTitle>Per Periode</CardTitle></CardHeader><CardContent>
            <Table><TableHeader><TableRow><TableHead>Periode</TableHead><TableHead>Status</TableHead><TableHead>Karyawan</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Deduksi</TableHead><TableHead className="text-right">Net</TableHead><TableHead className="text-right">PPh21</TableHead></TableRow></TableHeader>
              <TableBody>{d.periods.map((p) => (
                <TableRow key={p.period_id}><TableCell>{p.period_key}</TableCell><TableCell><Badge className={statusColor(p.status)}>{p.status}</Badge></TableCell><TableCell>{p.employee_count}</TableCell><TableCell className="text-right">{rp(p.gross)}</TableCell><TableCell className="text-right">{rp(p.deduction)}</TableCell><TableCell className="text-right font-semibold">{rp(p.net)}</TableCell><TableCell className="text-right">{rp(p.pph21)}</TableCell></TableRow>
              ))}</TableBody></Table>
          </CardContent></Card>
        </>
      )}
      {d && d.data_not_available && (
        <div className="text-sm text-slate-500 italic" data-testid="payroll-data-na">Belum ada periode payroll untuk tahun {year}. Buat periode payroll terlebih dahulu.</div>
      )}
    </div>
  );
}

// ============ Components master ============
export function PayrollComponents() {
  const [rows, setRows] = useState([]);
  useEffect(() => { API.get("/payroll/components").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="payroll-components-page">
      <h1 className="text-2xl font-bold">Komponen Payroll ({rows.length})</h1>
      <Card><CardContent className="pt-4">
        <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Type</TableHead><TableHead>Perhitungan</TableHead><TableHead>Taxable</TableHead><TableHead>BPJS Basis</TableHead><TableHead>Versi</TableHead><TableHead>Aktif</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((c) => (
            <TableRow key={c.id} data-testid={`comp-row-${c.code}`}><TableCell className="font-mono">{c.code}</TableCell><TableCell>{c.name}</TableCell><TableCell><Badge variant={c.type === "EARNING" ? "default" : "destructive"}>{c.type}</Badge></TableCell><TableCell>{c.calculation}</TableCell><TableCell>{c.taxable ? "✓" : "-"}</TableCell><TableCell>{c.bpjs_basis ? "✓" : "-"}</TableCell><TableCell>v{c.version}</TableCell><TableCell>{c.active ? "✓" : "-"}</TableCell></TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Salary Structure ============
export function SalaryStructurePage() {
  const [rows, setRows] = useState([]);
  const [comps, setComps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", effective_from: "", lines: [] });
  useEffect(() => {
    API.get("/payroll/salary-structures").then((r) => setRows(r.data)).catch(() => {});
    API.get("/payroll/components").then((r) => setComps(r.data.filter((c) => c.type === "EARNING" && c.active))).catch(() => {});
    API.get("/hr/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);
  const submit = async () => {
    try {
      await API.post("/payroll/salary-structures", form);
      toast.success("Struktur gaji tersimpan");
      const r = await API.get("/payroll/salary-structures");
      setRows(r.data);
      setForm({ employee_id: "", effective_from: "", lines: [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="salary-structure-page">
      <h1 className="text-2xl font-bold">Struktur Gaji</h1>
      <Card><CardHeader><CardTitle>Buat Struktur Baru</CardTitle></CardHeader><CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Karyawan</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="emp-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
              <SelectContent>{employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.code} — {e.full_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Berlaku Sejak</Label><Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="eff-from-input" /></div>
        </div>
        <div>
          <Label>Komponen</Label>
          {form.lines.map((l, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <Select value={l.component_code} onValueChange={(v) => { const nl = [...form.lines]; nl[i].component_code = v; setForm({ ...form, lines: nl }); }}>
                <SelectTrigger className="flex-1"><SelectValue placeholder="Komponen" /></SelectTrigger>
                <SelectContent>{comps.map((c) => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
              <Input type="number" placeholder="Nominal" value={l.amount} onChange={(e) => { const nl = [...form.lines]; nl[i].amount = parseFloat(e.target.value) || 0; setForm({ ...form, lines: nl }); }} className="w-40" />
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({ ...form, lines: [...form.lines, { component_code: "", amount: 0 }] })} data-testid="add-line-btn">+ Tambah Komponen</Button>
        </div>
        <Button onClick={submit} disabled={!form.employee_id || !form.effective_from || form.lines.length === 0} data-testid="save-salary-btn">Simpan</Button>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Daftar Struktur Gaji</CardTitle></CardHeader><CardContent>
        <Table><TableHeader><TableRow><TableHead>Karyawan</TableHead><TableHead>Berlaku</TableHead><TableHead>Versi</TableHead><TableHead>Status</TableHead><TableHead>Komponen</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => (<TableRow key={r.id}><TableCell>{r.employee_code}</TableCell><TableCell>{r.effective_from} — {r.effective_until || "…"}</TableCell><TableCell>v{r.version}</TableCell><TableCell><Badge className={statusColor(r.status)}>{r.status}</Badge></TableCell><TableCell>{r.lines.length} baris</TableCell></TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Periode Payroll ============
export function PayrollPeriodsPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const reload = () => API.get("/payroll/periods").then((r) => setRows(r.data)).catch(() => {});
  useEffect(reload, []);
  const create = async () => {
    try { await API.post("/payroll/periods", form); toast.success("Periode dibuat"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const doAction = async (pid, endpoint) => {
    try { const r = await API.post(`/payroll/periods/${pid}/${endpoint}`); toast.success(r.data.status || "OK"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="payroll-periods-page">
      <h1 className="text-2xl font-bold">Periode Payroll</h1>
      <Card><CardHeader><CardTitle>Buat Periode</CardTitle></CardHeader><CardContent className="flex gap-3 items-end">
        <div><Label>Tahun</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} className="w-24" data-testid="new-period-year" /></div>
        <div><Label>Bulan</Label><Input type="number" min="1" max="12" value={form.month} onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) })} className="w-20" data-testid="new-period-month" /></div>
        <Button onClick={create} data-testid="create-period-btn">Buat Periode</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4">
        <Table><TableHeader><TableRow><TableHead>Periode</TableHead><TableHead>Status</TableHead><TableHead>Emp</TableHead><TableHead>Journal</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((p) => (
            <TableRow key={p.id} data-testid={`period-row-${p.period_key}`}>
              <TableCell><Link to={`/payroll/periode/${p.id}`} className="text-blue-600 underline">{p.period_key}</Link></TableCell>
              <TableCell><Badge className={statusColor(p.status)}>{p.status}</Badge></TableCell>
              <TableCell>{p.employee_count || "-"}</TableCell>
              <TableCell className="font-mono text-xs">{p.journal_number || "-"}</TableCell>
              <TableCell className="space-x-1">
                {p.status === "DRAFT" && <Button size="sm" onClick={() => doAction(p.id, "calculate")} data-testid={`calc-${p.period_key}`}>Kalkulasi</Button>}
                {p.status === "CALCULATED" && <Button size="sm" onClick={() => doAction(p.id, "submit-review")} data-testid={`review-${p.period_key}`}>Ajukan Review</Button>}
                {p.status === "REVIEW" && <Button size="sm" onClick={() => doAction(p.id, "approve")} data-testid={`approve-${p.period_key}`}>Setujui</Button>}
                {p.status === "APPROVED" && <Button size="sm" onClick={() => doAction(p.id, "post")} data-testid={`post-${p.period_key}`}>Post GL</Button>}
                {(p.status === "POSTED" || p.status === "PAID") && <Button size="sm" variant="outline" onClick={() => doAction(p.id, "lock")}>Lock</Button>}
              </TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Periode Detail (drill-down) ============
export function PayrollPeriodDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    API.get(`/payroll/periods/${pid}`).then((r) => setP(r.data)).catch(() => {});
    API.get(`/payroll/periods/${pid}/journal-preview`).then((r) => setPreview(r.data)).catch(() => {});
  }, [pid]);
  if (!p) return <div className="p-6">Memuat…</div>;
  return (
    <div className="p-6 space-y-4" data-testid="period-detail-page">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold">Periode {p.period_key}</h1>
        <Badge className={statusColor(p.status)}>{p.status}</Badge>
      </div>
      <Card><CardHeader><CardTitle>Payroll Runs ({p.run_count})</CardTitle></CardHeader><CardContent>
        <Table><TableHeader><TableRow><TableHead>Karyawan</TableHead><TableHead>Gross</TableHead><TableHead>Deduksi</TableHead><TableHead>PPh21</TableHead><TableHead>BPJS</TableHead><TableHead>Net</TableHead><TableHead>Ver</TableHead><TableHead>Slip</TableHead></TableRow></TableHeader>
          <TableBody>{(p.runs || []).map((r) => {
            const pph = r.deductions?.find((x) => x.component_code === "PPH21")?.amount || 0;
            const bpjs = (r.deductions?.find((x) => x.component_code === "BPJS_KES_EMP")?.amount || 0) + (r.deductions?.find((x) => x.component_code === "BPJS_TK_EMP")?.amount || 0);
            return (<TableRow key={r.id} data-testid={`run-${r.employee_code}`}>
              <TableCell>{r.employee_code}<br /><span className="text-xs text-slate-500">{r.employee_name}</span></TableCell>
              <TableCell>{rp(r.gross)}</TableCell><TableCell>{rp(r.total_deduction)}</TableCell><TableCell>{rp(pph)}</TableCell><TableCell>{rp(bpjs)}</TableCell><TableCell className="font-semibold">{rp(r.net)}</TableCell>
              <TableCell className="text-xs">SS v{r.salary_structure_version}<br />Tax v{r.tax_rule_version}</TableCell>
              <TableCell><a href={`${process.env.REACT_APP_BACKEND_URL}/api/payroll/payslip/${r.id}/pdf`} target="_blank" rel="noreferrer" className="text-blue-600 underline">PDF</a></TableCell>
            </TableRow>);
          })}</TableBody></Table>
      </CardContent></Card>
      {preview && (
        <Card><CardHeader><CardTitle>Journal Preview {preview.balanced ? <Badge className="bg-emerald-500">Balanced</Badge> : <Badge className="bg-red-600">Tidak Seimbang</Badge>}</CardTitle></CardHeader><CardContent>
          <Table><TableHeader><TableRow><TableHead>Akun</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Db</TableHead><TableHead className="text-right">Cr</TableHead></TableRow></TableHeader>
            <TableBody>{preview.lines.map((l, i) => (
              <TableRow key={i}><TableCell className="font-mono">{l.account_code}</TableCell><TableCell>{l.description}</TableCell><TableCell className="text-right">{rp(l.debit)}</TableCell><TableCell className="text-right">{rp(l.credit)}</TableCell></TableRow>
            ))}<TableRow className="font-bold"><TableCell colSpan={2}>TOTAL</TableCell><TableCell className="text-right">{rp(preview.totals.debit)}</TableCell><TableCell className="text-right">{rp(preview.totals.credit)}</TableCell></TableRow></TableBody></Table>
        </CardContent></Card>
      )}
    </div>
  );
}

// ============ Loan ============
export function LoanPage() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", principal: 0, installment_count: 6, start_period: "" });
  const reload = () => API.get("/payroll/loans").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); API.get("/hr/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try { await API.post("/payroll/loans", form); toast.success("Loan dibuat"); reload(); setForm({ employee_id: "", principal: 0, installment_count: 6, start_period: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="loan-page">
      <h1 className="text-2xl font-bold">Pinjaman / Kasbon</h1>
      <Card><CardHeader><CardTitle>Buat Loan Baru</CardTitle></CardHeader><CardContent className="grid grid-cols-4 gap-3 items-end">
        <div><Label>Karyawan</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger data-testid="loan-emp"><SelectValue /></SelectTrigger>
            <SelectContent>{employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.code}</SelectItem>))}</SelectContent>
          </Select></div>
        <div><Label>Principal</Label><Input type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: parseFloat(e.target.value) || 0 })} data-testid="loan-principal" /></div>
        <div><Label>Cicilan</Label><Input type="number" value={form.installment_count} onChange={(e) => setForm({ ...form, installment_count: parseInt(e.target.value) })} /></div>
        <div><Label>Mulai Periode (YYYY-MM)</Label><Input value={form.start_period} onChange={(e) => setForm({ ...form, start_period: e.target.value })} placeholder="2026-01" /></div>
        <Button onClick={submit} data-testid="save-loan-btn">Simpan</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4">
        <Table><TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Karyawan</TableHead><TableHead>Kind</TableHead><TableHead className="text-right">Principal</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Cicilan</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((l) => (<TableRow key={l.id} data-testid={`loan-${l.number}`}><TableCell className="font-mono">{l.number}</TableCell><TableCell>{l.employee_code}</TableCell><TableCell><Badge>{l.kind}</Badge></TableCell><TableCell className="text-right">{rp(l.principal)}</TableCell><TableCell className="text-right">{rp(l.outstanding)}</TableCell><TableCell className="text-right">{rp(l.installment_amount)}</TableCell><TableCell><Badge className={statusColor(l.status)}>{l.status}</Badge></TableCell></TableRow>))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Payment Batch ============
export function PaymentBatchPage() {
  const [rows, setRows] = useState([]);
  const reload = () => API.get("/payroll/payment-batches").then((r) => setRows(r.data)).catch(() => {});
  useEffect(reload, []);
  const doAction = async (bid, endpoint) => {
    try { const r = await API.post(`/payroll/payment-batches/${bid}/${endpoint}`); toast.success(r.data.status); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="payment-batch-page">
      <h1 className="text-2xl font-bold">Payment Batch</h1>
      <Card><CardContent className="pt-4">
        <Table><TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Periode</TableHead><TableHead>Bank</TableHead><TableHead>Karyawan</TableHead><TableHead className="text-right">Total Net</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((b) => (
            <TableRow key={b.id} data-testid={`batch-${b.number}`}>
              <TableCell className="font-mono">{b.number}</TableCell><TableCell>{b.period_key}</TableCell><TableCell>{b.bank_account_name}</TableCell><TableCell>{b.employee_count}</TableCell><TableCell className="text-right">{rp(b.total_net)}</TableCell>
              <TableCell><Badge className={statusColor(b.status)}>{b.status}</Badge></TableCell>
              <TableCell className="space-x-1">
                {b.status === "DRAFT" && <Button size="sm" onClick={() => doAction(b.id, "submit-review")}>Ajukan</Button>}
                {b.status === "REVIEW" && <Button size="sm" onClick={() => doAction(b.id, "approve")}>Setujui</Button>}
                {b.status === "APPROVED" && <Button size="sm" onClick={() => doAction(b.id, "ready")}>Siap Bayar</Button>}
                {b.status === "READY_TO_PAY" && <Button size="sm" onClick={() => doAction(b.id, "mark-paid")} data-testid={`pay-${b.number}`}>Tandai Paid</Button>}
              </TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Slip Gaji (ESS) ============
export function MyPayslipPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { API.get("/payroll/my-payslips").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="my-payslip-page">
      <h1 className="text-2xl font-bold">Slip Gaji Saya</h1>
      {rows.length === 0 ? <Card><CardContent className="pt-4 text-slate-500">Belum ada slip gaji tersedia atau akun Anda belum terhubung ke karyawan.</CardContent></Card> :
        <Table><TableHeader><TableRow><TableHead>Periode</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Deduksi</TableHead><TableHead className="text-right">Net</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => (<TableRow key={r.id}><TableCell>{r.period_key}</TableCell><TableCell className="text-right">{rp(r.gross)}</TableCell><TableCell className="text-right">{rp(r.total_deduction)}</TableCell><TableCell className="text-right font-semibold">{rp(r.net)}</TableCell><TableCell><a href={`${process.env.REACT_APP_BACKEND_URL}/api/payroll/payslip/${r.id}/pdf`} target="_blank" rel="noreferrer" className="text-blue-600 underline" data-testid={`my-pdf-${r.period_key}`}>PDF</a></TableCell></TableRow>))}</TableBody></Table>}
    </div>
  );
}

// ============ Integrity Center ============
export function PayrollIntegrityPage() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/payroll/integrity").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-6">Memuat…</div>;
  return (
    <div className="p-6 space-y-4" data-testid="payroll-integrity-page">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Integritas Payroll</h1>
        <Badge className={statusColor(d.overall)}>{d.overall}</Badge>
      </div>
      <Card><CardContent className="pt-4">
        <Table><TableHeader><TableRow><TableHead>#</TableHead><TableHead>Nama</TableHead><TableHead>Status</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
          <TableBody>{d.checks.map((c, i) => (
            <TableRow key={i} data-testid={`integrity-check-${i}`}>
              <TableCell>{i + 1}</TableCell><TableCell>{c.name}</TableCell><TableCell><Badge className={statusColor(c.status)}>{c.status}</Badge></TableCell>
              <TableCell className="text-xs font-mono">{Object.entries(c).filter(([k]) => !["name", "status"].includes(k)).map(([k, v]) => `${k}=${v}`).join(", ")}</TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}
